import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import LeftPanel from '../components/LeftPanel';
import PrimaryButton from '../components/PrimaryButton';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Alert from '@mui/material/Alert';
import { sendSupportTicket } from '../api/support';
import api from '../api';

function ForgotPassword() {
    const [email, setEmail] = useState('');
    const [sending, setSending] = useState(false);
    const [message, setMessage] = useState('');
    const navigate = useNavigate();

    const [openSupport, setOpenSupport] = useState(false);
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [schoolName, setSchoolName] = useState('');
    const [supportEmail, setSupportEmail] = useState('');
    const [supportType, setSupportType] = useState('');
    const [supportReason, setSupportReason] = useState('');
    const [supportLoading, setSupportLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, severity: 'success', message: '' });

    // Step 1: request OTP for existing email. On success navigate to OTP page.
    const handleSendOtp = async (e) => {
        e.preventDefault();
        setMessage('');
        const normalized = (email || '').trim().toLowerCase();
        if (!normalized || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized)) {
            setMessage('Please enter a valid email address.');
            return;
        }

        setSending(true);
        try {
            // Server should respond with 200 when OTP sent, or 404 / 400 when email not found.
            await api.post('/api/auth/forgot-password', null, { params: { email: normalized } });
            // Navigate to the OTP page and let that page handle OTP entry and resetting.
            try { localStorage.setItem('pendingEmail', normalized); } catch (e) { }
            navigate('/otp', { state: { email: normalized, flow: 'forgot-password' } });
        } catch (err) {
            const status = err?.response?.status;
            const data = err?.response?.data;
            const serverMessage = typeof data === 'string' ? data : data?.message;
            const looksLikeHtml = typeof serverMessage === 'string' && /<\/?(html|body|doctype)/i.test(serverMessage);

            if (status === 404 || status === 403) {
                setMessage('We\'re sorry, the email address you entered doesn\'t exist in our system, please try entering it again.');
            } else if (serverMessage && !looksLikeHtml) {
                // Show plain-text or JSON message only; avoid exposing HTML error pages.
                setMessage(serverMessage);
            } else {
                setMessage('We\'re sorry, the email address you entered doesn\'t exist in our system, please try entering it again.');
            }
        } finally {
            setSending(false);
        }
    };

    const handleCloseSupport = () => {
        setOpenSupport(false);
    };
    const handleSubmitSupport = async () => {
        setSupportLoading(true);
        try {
            await sendSupportTicket({ firstName, lastName, schoolName, email: supportEmail, supportType, additionalInformation: supportReason });
            setSnackbar({ open: true, severity: 'success', message: 'Support request sent!' });
            setTimeout(() => {
                setSnackbar({ open: false, severity: 'success', message: '' });
                setOpenSupport(false);
            }, 1500);
        } catch (err) {
            setSnackbar({ open: true, severity: 'error', message: err?.response?.data?.message || 'Failed to send support request' });
        } finally {
            setSupportLoading(false);
        }
    };

    return (
        <Box component="main">
            <Grid container spacing={4} sx={{ mt: 2 }}>
                <Grid item xs={12} md={4} lg={3}>
                    <LeftPanel items={["Enter your email to receive an OTP.", "You will be redirected to enter the OTP and reset your password.", "Contact support if you face issues."]}>
                        <PrimaryButton sx={{ bgcolor: '#fff', color: '#000', textTransform: 'none' }} onClick={() => {
                            setSnackbar({ open: false, severity: 'success', message: '' });
                            setSupportReason('');
                            setSupportType('');
                            setFirstName('');
                            setLastName('');
                            setSchoolName('');
                            setSupportEmail('');
                            setOpenSupport(true);
                        }}>Contact Support</PrimaryButton>
                    </LeftPanel>
                </Grid>
                <Grid item xs={12} md={8} lg={6}>
                    <Paper elevation={1} sx={{ p: 0, overflow: 'hidden' }}>
                        <Box sx={{ bgcolor: 'black', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', px: 3, py: { xs: 1.5, md: 2 } }}>
                            <Box
                                component="img"
                                src="/ryslogo.png"
                                alt="Retool Your School"
                                sx={{ height: { xs: 42, md: 60 }, objectFit: 'contain' }}
                            />
                        </Box>
                        <Box sx={{ p: { xs: 3, md: 4 } }}>
                            <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>Reset Password</Typography>

                            <Box component="form" onSubmit={handleSendOtp} noValidate>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <TextField label="Email" value={email} onChange={e => setEmail(e.target.value)} fullWidth required />
                                    </Grid>
                                    {message && (
                                        <Grid item xs={12}>
                                            <Typography color="error">{message}</Typography>
                                        </Grid>
                                    )}
                                    <Grid item xs={12}>
                                        <PrimaryButton type="submit" disabled={sending}>Reset Password</PrimaryButton>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Box>
                    </Paper>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                        <Typography
                            variant="body2"
                            onClick={() => navigate('/login')}
                            sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                        >
                            Return to the login page
                        </Typography>
                    </Box>
                </Grid>
            </Grid>
            <Dialog open={openSupport} onClose={handleCloseSupport} fullWidth maxWidth="sm">
                <DialogTitle>Support Request</DialogTitle>
                <DialogContent>
                    {snackbar.open && (
                        <Alert severity={snackbar.severity} sx={{ mb: 2 }}>
                            {snackbar.message}
                        </Alert>
                    )}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField label="First name" value={firstName} onChange={(e) => setFirstName(e.target.value)} fullWidth required InputLabelProps={{ sx: { '& .MuiFormLabel-asterisk': { color: '#f57c00' } } }} />
                        <TextField label="Last name" value={lastName} onChange={(e) => setLastName(e.target.value)} fullWidth required InputLabelProps={{ sx: { '& .MuiFormLabel-asterisk': { color: '#f57c00' } } }} />
                        <TextField label="School name" value={schoolName} onChange={(e) => setSchoolName(e.target.value)} fullWidth required InputLabelProps={{ sx: { '& .MuiFormLabel-asterisk': { color: '#f57c00' } } }} />
                        <TextField label="Email" value={supportEmail} onChange={(e) => setSupportEmail(e.target.value)} fullWidth required InputLabelProps={{ sx: { '& .MuiFormLabel-asterisk': { color: '#f57c00' } } }} />
                        <FormControl fullWidth required>
                            <InputLabel id="support-type-label" sx={{ '& .MuiFormLabel-asterisk': { color: '#f57c00' } }}>Choose your type of support</InputLabel>
                            <Select labelId="support-type-label" value={supportType} label={"Choose your type of support"} onChange={(e) => setSupportType(e.target.value)}>
                                <MenuItem value={"My school is not eligible"}>My school is not eligible</MenuItem>
                                <MenuItem value={"I'm having trouble with my application"}>I'm having trouble with my application</MenuItem>
                                <MenuItem value={"I'm having trouble inviting a collaborator"}>I'm having trouble inviting a collaborator</MenuItem>
                                <MenuItem value={"I didn't receive my email verification"}>I didn't receive my email verification</MenuItem>
                                <MenuItem value={"Other"}>Other</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField label="Additional information" value={supportReason} onChange={e => setSupportReason(e.target.value)} fullWidth multiline minRows={3} required InputLabelProps={{ sx: { '& .MuiFormLabel-asterisk': { color: '#f57c00' } } }} />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <PrimaryButton onClick={handleCloseSupport} disabled={supportLoading}>Cancel</PrimaryButton>
                    <PrimaryButton onClick={handleSubmitSupport} variant="contained" disabled={supportLoading || !supportReason.trim() || !firstName.trim() || !lastName.trim() || !schoolName.trim() || !supportEmail.trim() || !supportType}>
                        {supportLoading ? 'Sending...' : 'Send'}
                    </PrimaryButton>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
export default ForgotPassword;

